import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api/api";
import ProductCard from "../components/ProductCard";
import CategoryCard from "../components/CategoryCard";
import Breadcrumb from "../components/Breadcrumb";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import LoadMoreButton from "../components/LoadMoreButton";
import { getCached, setCached, isFresh } from "../utils/apiCache";
import "../styles/components/Products.scss";
import "../styles/components/Categories.scss";
import "../styles/components/CategoryPage.scss";

function CategoryPage() {
  const { id, childId } = useParams();
  const effectiveId = childId || id;

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    // Only the first page is cached - "Load More" always hits the network,
    // since it's a one-directional accumulation the visitor explicitly
    // asked for, not a revisit.
    const cacheKey = `category:${effectiveId}`;

    const applyData = (data) => {
      setCategory(data?.category || null);
      setProducts(Array.isArray(data?.data) ? data.data : []);
      setMeta(data?.meta || null);
    };

    const cached = getCached(cacheKey);
    if (cached) {
      applyData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError("");

    const fetchCategory = async () => {
      try {
        const res = await api.get(`/categories/${effectiveId}`);

        if (!active) return;
        setCached(cacheKey, res.data);
        applyData(res.data);
      } catch (err) {
        console.error(err);
        if (active && !cached) setError("Category not found");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (!isFresh(cached)) {
      fetchCategory();
    }

    return () => {
      active = false;
    };
  }, [effectiveId]);

  const hasMore = Boolean(meta && meta.current_page < meta.last_page);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      const res = await api.get(`/categories/${effectiveId}`, {
        params: { page: meta.current_page + 1 },
      });

      setProducts((prev) => [...prev, ...(Array.isArray(res.data?.data) ? res.data.data : [])]);
      setMeta(res.data?.meta || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const children = category?.children || [];
  const parent = category?.parent || null;

  const breadcrumbItems = category
    ? [
        { label: "Home", to: "/" },
        ...(parent ? [{ label: parent.name, to: `/c/${parent.id}` }] : []),
        { label: category.name },
      ]
    : [];

  return (
    <div className="category-page reveal">
      {loading ? (
        <Loader message="Loading category..." />
      ) : error || !category ? (
        <EmptyState message={error || "Category not found"} variant="error" />
      ) : (
        <>
          <div className="page-head">
            <Breadcrumb items={breadcrumbItems} />
            <h1
              className={`page-title page-title--eyebrow${
                parent ? "" : " page-title--lg"
              }`}
            >
              {category.name}
            </h1>
          </div>

          {/* A category with subcategories shows only its subcategory tiles;
              a leaf category shows only its products. Never both at once,
              so entering "Wall Arts" as a subcategory doesn't also dump its
              own products underneath it a second time. */}
          {children.length > 0 ? (
            <div className="categories-grid category-page-subgrid">
              {children.map((child) => (
                <CategoryCard
                  key={child.id}
                  category={child}
                  linkTo={`/c/${category.id}/${child.id}`}
                />
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {hasMore && (
                <LoadMoreButton onClick={handleLoadMore} loading={loadingMore} />
              )}
            </>
          ) : (
            <EmptyState message="No products in this category yet" />
          )}
        </>
      )}
    </div>
  );
}

export default CategoryPage;
